import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Checkbox,
  Avatar,
  List,
  ListItem,
  Button,
  Snackbar,
  Alert,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useTranslation } from "react-i18next";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  getJobsByEmplacement,
  saveJobsSelection,
  createJob,
  updateJob,
  deleteJob,
} from "../Jobs/api/jobs.api";

import "../../../css/JobSelectorPage.css";
import AddJobDialog from "./modal/AddJobDialog";
import EditJobDialog from "./modal/EditJobDialog";

export default function JobSelectorPage() {
  // ================= Redux =================
  const userState = useSelector((state) => state.user);
  const token = userState.token;
  const emplacementId = userState?.location?.noEmplacement;
  const { t, i18n } = useTranslation(["Jobs"]);
  const isFrench = i18n.language?.toLowerCase().startsWith("fr");
  // ================= State =================
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success | error | warning | info
  });
  const [saving, setSaving] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  // ================= show message =================
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // ================= Load Jobs =================
  const loadJobs = async () => {
    if (!emplacementId) return;

    try {
      setLoading(true);

      const data = await getJobsByEmplacement(token, emplacementId);

      setJobs(data);

      const selectedIds = data.filter((x) => x.isSelected).map((x) => x.id);

      setSelected(selectedIds);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, [emplacementId]);

  // ================= Filter =================
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const text = `${job.nomFR} ${job.nomEN}`.toLowerCase();
      return text.includes(search.toLowerCase());
    });
  }, [jobs, search]);

  // ================= Toggle =================
  const toggleJob = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAll = () => {
    setSelected(filteredJobs.map((j) => j.id));
  };

  const unselectAll = () => {
    setSelected([]);
  };

  // ================= SAVE =================
  const handleSave = async () => {
    try {
      setSaving(true);

      const response = await saveJobsSelection(token, emplacementId, selected);

      if (response.success) {
        showSnackbar(response.messageFr || t("messages.jobsSaved"), "success");

        await loadJobs();
      } else {
        showSnackbar(response.messageFr || t("messages.saveError"));
      }
    } catch (error) {
      console.error(error);
      showSnackbar(t("messages.serverError"), "error");
    } finally {
      setSaving(false);
    }
  };

  // ================= ACTIONS =================
  // ================= EDIT =================
  const handleEdit = (job) => {
    setSelectedJob(job);
    setOpenEdit(true);
  };

  const handleUpdateJob = async (job, form) => {
    try {
      const res = await updateJob(token, job.id, form);

      if (res.success) {
        showSnackbar(res.messageFr || t("messages.jobUpdated"), "success");

        await loadJobs(); // 🔥 refresh backend
        setOpenEdit(false);
        setSelectedJob(null);
      } else {
        showSnackbar(res.messageFr || t("messages.updateError", "error"));
      }
    } catch (err) {
      console.error(err);
      showSnackbar(t("messages.serverError"), "error");
    }
  };
  const confirmDeleteJob = async () => {
    if (!jobToDelete) return;

    try {
      const res = await deleteJob(token, jobToDelete.id);

      if (res.success) {
        showSnackbar(res.messageFr || t("messages.jobDeleted"), "success");

        // Refresh la liste
        await loadJobs();

        // Retirer de la sélection
        setSelected((prev) => prev.filter((id) => id !== jobToDelete.id));
      } else {
        showSnackbar(res.messageFr || t("messages.deleteError"), "error");
      }
    } catch (err) {
      console.error(err);

      showSnackbar(err?.messageFr || t("messages.serverError"), "error");
    } finally {
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    }
  };

  const handleDelete = (job) => {
    setJobToDelete(job);
    setDeleteDialogOpen(true);
  };
  // ================= ADD =================
  const handleOpenAdd = () => {
    setOpenAdd(true);
  };

  const handleAddJob = async (form) => {
    try {
      const res = await createJob(token, form);

      if (res.success) {
        showSnackbar(res.messageFr || t("messages.jobCreated"), "success");

        await loadJobs();
        setOpenAdd(false);
      } else {
        showSnackbar(res.messageFr || t("messages.createError"), "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar(t("messages.serverError"), "error");
    }
  };

  // ================= UI =================
  return (
    <Box sx={{ p: 3, pb: 10 }}>
      {/* ================= HEADER ================= */}
      <Card
        className="mh-header-card"
        sx={{
          mb: 3,
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <Box className="mh-header-topbar" />

        <CardContent sx={{ p: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {t("page.title")}
              </Typography>

              <Typography variant="body1" color="text.secondary">
                {t("page.subtitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                👉 Id: {userState?.location?.noEmplacement}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                📍 {userState?.location?.nomEmplacement}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("page.jobsAvailable", { count: jobs.length })}
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              startIcon={<AddCircleOutlineIcon />}
              sx={{ borderRadius: 3, display: "none" }}
              onClick={handleOpenAdd}
            >
              {t("buttons.addJob")}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ================= SEARCH ================= */}
      <Grid container sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("search.label")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {/* ================= ACTIONS ================= */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={selectAll}>
          {t("buttons.selectAll")}
        </Button>

        <Button variant="outlined" onClick={unselectAll}>
          {t("buttons.unselectAll")}
        </Button>
      </Stack>

      {/* ================= LIST ================= */}
      <Card sx={{ borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            <Typography>{t("list.loading")}</Typography>
          </Box>
        ) : (
          <List>
            {filteredJobs.map((job) => (
              <ListItem
                key={job.id}
                divider
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&:hover": {
                    backgroundColor: "#f5f7fb",
                  },
                }}
              >
                {/* LEFT SIDE */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar sx={{ bgcolor: "#1976d2" }}>
                    <WorkOutlineIcon />
                  </Avatar>

                  <Box>
                    <Typography fontWeight={600}>
                      {isFrench ? job.nomFR : job.nomEN}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {isFrench ? job.nomEN : job.nomFR}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      {t("list.jobId")}: {job.id}
                    </Typography>
                  </Box>
                </Box>

                {/* RIGHT SIDE (NEW ACTIONS + CHECKBOX) */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    checked={selected.includes(job.id)}
                    onChange={() => toggleJob(job.id)}
                  />

                  <Tooltip title={t("tooltips.editJob")}>
                    <IconButton
                      sx={{
                        display: "none",
                      }}
                      size="small"
                      onClick={() => handleEdit(job)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("tooltips.deleteJob")}>
                    <IconButton
                      size="small"
                      color="error"
                      sx={{
                        display: "none",
                      }}
                      onClick={() => handleDelete(job)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Card>

      {/* ================= SAVE BAR ================= */}
      <AppBar
        position="fixed"
        sx={{
          top: "auto",
          bottom: 0,
          background: "#fff",
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2">
              {t("list.selectedJobs", { count: selected.length })}
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !emplacementId}
          >
            {saving ? t("buttons.saving") : t("buttons.save")}
          </Button>
        </Toolbar>
      </AppBar>

      {/* ================= modal add/edit ================= */}
      <AddJobDialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        onSave={handleAddJob}
      />
      <EditJobDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        job={selectedJob}
        onUpdate={handleUpdateJob}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 5,
            width: 420,
            p: 3,
            textAlign: "center",
            background: "#fff",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <WarningAmberRoundedIcon
            sx={{
              fontSize: 60,
              color: "#ff9800",
              background: "#fff3e0",
              borderRadius: "50%",
              p: 1.5,
            }}
          />
        </Box>

        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: 20,
          }}
        >
          {t("messages.deleteJob")}
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{
              fontSize: 14,
              color: "#666",
            }}
          >
            {t("messages.deleteJobConfirm")}{" "}
            <strong>
              {i18n.language.startsWith("fr")
                ? jobToDelete?.nomFR
                : jobToDelete?.nomEN}
            </strong>
            ?
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 2,
            mt: 2,
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 3,
              px: 3,
              textTransform: "none",
            }}
          >
            {t("buttons.cancel")}
          </Button>

          <Button
            onClick={confirmDeleteJob}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 3,
              px: 3,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ================= SNACKBARS ================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
